import { MemberReviewDetailResponse } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import React, { useMemo } from 'react';
import SvgIcon from '@/components/SvgIcon';
import dayjs from 'dayjs';
import { GradientStars } from '@/components/RatingStars/RatingStars';
import { TooltipWrapper } from '@/components/TooltipWrapper';

type Props = {
    detail: MemberReviewDetailResponse;
    reviewStageLabel: string;
    handlePrev: () => void;
    handleNext: () => void;
};

function ModalMemberReviewContent({ detail, reviewStageLabel, handlePrev, handleNext }: Props) {
    const reviewCreatedAt = detail?.reviewCreatedAt
        ? `${dayjs(detail.reviewCreatedAt).format('YYYY-MM-DD(ddd) HH:mm')} 작성`
        : '-';

    const questions = useMemo(() => detail?.questionList ?? [], [detail]);

    return (
        <Col className="flex relative">
            {/* 작성 날짜 */}
            <DemiLightText fontSize={13} lineHeight={18} color={'#8C8F93'} className="absolute top-[15px] right-0">
                {reviewCreatedAt}
            </DemiLightText>
            {/* 회차 네비게이션 및 별점 */}
            <Col className="items-center mb-[45px] mt-[33px]">
                <Row className="items-center justify-center gap-[24px] mb-[16px]">
                    <button
                        onClick={handlePrev}
                        disabled={!detail.prevReviewId}
                        className="w-[32px] h-[32px] rounded-full bg-[#2D3132] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#3D4244] transition-colors"
                    >
                        <SvgIcon
                            name="SvgLeftStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="white"
                            strokeWidth={2}
                        />
                    </button>

                    <MediumText fontSize={28} lineHeight={37} color={colors.Text_1}>
                        {reviewStageLabel}
                    </MediumText>

                    <button
                        onClick={handleNext}
                        disabled={!detail.nextReviewId}
                        className="w-[32px] h-[32px] rounded-full bg-[#2D3132] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#3D4244] transition-colors"
                    >
                        <SvgIcon
                            name="SvgRightStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="white"
                            strokeWidth={2}
                        />
                    </button>
                </Row>

                {/* 큰 별점 */}
                <Col className="items-center gap-[5px]">
                    <MediumText fontSize={30} lineHeight={40} color={colors.Text_1}>
                        {detail.reviewAverageRating.toFixed(1)}
                    </MediumText>
                    <GradientStars rating={detail.reviewAverageRating} size={20} gap={0} showOnlyFilled={false} />
                </Col>
            </Col>

            {/* 질문 카드들 */}
            <Col className="gap-[32px]">
                {questions.map((question) => (
                    <QuestionCard key={question.questionCode} question={question} />
                ))}
            </Col>
        </Col>
    );
}

type QuestionCardProps = {
    question: MemberReviewDetailResponse['questionList'][number];
};

function QuestionCard({ question }: QuestionCardProps) {
    const isNeeds = question.questionType === 'needs';
    const answerList = question.answerList || [];

    return (
        <Col className="p-[24px] bg-white rounded-[28px] border-solid border-[0.5px] border-[#B7B8BA]">
            {/* 질문 타입과 별점 */}
            {!isNeeds && question.questionRating !== null && question.questionRating !== undefined && (
                <Row className="items-center justify-center mb-[16px]">
                    <div className="flex px-[12px] h-[36px] bg-white rounded-full border-[0.5px] border-[#191F28] border-solid justify-center items-center">
                        <Row className="flex items-center justify-center ">
                            <RegularText
                                fontSize={14}
                                lineHeight={19}
                                color={'#191F28'}
                                className="mr-[5px]"
                                letterSpacing={-0.7}
                            >
                                {question.questionSubject}
                            </RegularText>
                            <SvgIcon name="SvgStar" fill="#3D73DD" size={10} className="mr-[3px]" />
                            <MediumText fontSize={13} lineHeight={18} color={colors.Text_1}>
                                {question.questionRating}
                            </MediumText>
                        </Row>
                    </div>
                </Row>
            )}
            {isNeeds && (
                <Row className="flex items-center justify-center mb-[16px] gap-[8px] ">
                    <div className="flex px-[12px] h-[36px] bg-white rounded-full border-[0.5px] border-[#191F28] border-solid justify-center items-center">
                        <RegularText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.7}>
                            맞춤요청
                        </RegularText>
                    </div>
                    {question.needsCategory && (
                        <div className="flex flex-row px-[12px] h-[36px] bg-white rounded-full border-[0.5px] border-[#191F28] border-solid justify-center items-center">
                            <SvgIcon
                                name="SvgCheck"
                                size={12}
                                fill={colors.trans}
                                stroke={colors.primary}
                                strokeWidth={1.75}
                                className="mr-[5px]"
                            />
                            <RegularText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.6}>
                                {question.needsCategory}
                            </RegularText>
                        </div>
                    )}
                </Row>
            )}

            {/* 질문 텍스트 */}
            <RegularText fontSize={18} lineHeight={25} color={'#191F28'} className="text-center mb-[32px]">
                {question.questionContent}
            </RegularText>

            {/* 답변들 */}
            <div className="flex flex-wrap gap-[14px] justify-start">
                {answerList.map((answer) => {
                    const isCustom = answer.isCustom;
                    const isPositive = answer.answerType === 'positive';
                    const isNegative = answer.answerType === 'negative';
                    const isRequest = answer.answerType === 'request';

                    const bgColor = isPositive ? '#E8F9F3' : isNegative ? '#FFF0F0' : isRequest ? '#EAF2FF' : '#F2F4F5';

                    const textColor = isPositive
                        ? '#12A25D'
                        : isNegative
                          ? '#E02C45'
                          : isRequest
                            ? '#3D73DD'
                            : colors.Text_1;

                    return isCustom ? (
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                customContent: (
                                    <Col>
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            {answer.answerContent}
                                        </DemiLightText>
                                    </Col>
                                ),
                            }}
                        >
                            <Row
                                key={answer.answerCode}
                                className="flex px-[16px] h-[44px] rounded-[12px] justify-center items-center"
                                style={{ backgroundColor: bgColor }}
                            >
                                <Col className="w-[22px] h-[22px] bg-[#4D4F57] absolute top-[-8px] left-[-8px] rounded-full justify-center items-center">
                                    <SvgIcon
                                        name="SvgPencil"
                                        size={10}
                                        fill={colors.white}
                                        stroke={colors.white}
                                        strokeWidth={1}
                                    />
                                </Col>
                                <RegularText
                                    fontSize={14}
                                    lineHeight={19}
                                    color={textColor}
                                    className="mr-[6px] "
                                    letterSpacing={-0.4}
                                >
                                    {answer.answerContent}
                                </RegularText>
                            </Row>
                        </TooltipWrapper>
                    ) : (
                        <Row
                            key={answer.answerCode}
                            className="flex px-[16px] h-[44px] rounded-[12px] justify-center items-center"
                            style={{ backgroundColor: bgColor }}
                        >
                            <RegularText
                                fontSize={14}
                                lineHeight={19}
                                color={textColor}
                                className="mr-[6px]"
                                letterSpacing={-0.4}
                            >
                                {answer.answerContent}
                            </RegularText>
                        </Row>
                    );
                })}
            </div>
        </Col>
    );
}

export default ModalMemberReviewContent;
